
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import Marvin from '@/components/svg/marvin/marvin_sleeping.svg?inline';
import { AvailabilityInterface } from '@/models/Availability';
import { ProfileStoreKey } from '@/models/Profile';
import { ExternalProject } from '@/models/Recommendations';
import recommendationService from '@/services/RecommendationService';
import useMessengerState from '@/state/messenger/messengerState';
import useProfileState from '@/state/profile/profileState';
import ProfileSidebarAvailability from '@/views/profile/components/sidebar/ProfileSidebarAvailability.vue';
import ProjectSelector from '@/views/project-forum/components/sidebar-actions/ProjectSelector.vue';
import ProfileSidebarProjectSummary from '@/views/profile/components/sidebar/ProfileSidebarProjectSummary.vue';
import {
  computed, defineComponent, nextTick, PropType, ref, toRefs, watch
} from 'vue';
import { useDebounce } from '@vueuse/core';
import RecommendWarningErrors from '@/views/recommendations/components/RecommendWarningErrors.vue';
import RecommendNotMatchingReasons from '@/views/recommendations/components/RecommendNotMatchingReasons.vue';
import RecommendedMessage from '@/views/recommendations/components/recommend-project-to-users/RecommendedMessage.vue';
import BCXSidebarHelpContainer from '@/components/molecules/BCXSidebarHelpContainer.vue';
import useRecommendation from '@/views/recommendations/composables/useRecommendation';
import useUser from '@/mixins/useUser';
import RecommendationMessageBox from '@/views/recommendations/components/RecommendationMessageBox.vue';

export default defineComponent({
  name: 'RecommendUserForProjects',
  components: {
    RecommendationMessageBox,
    BCXSidebarHelpContainer,
    RecommendedMessage,
    RecommendNotMatchingReasons,
    RecommendWarningErrors,
    Marvin,
    BCXMarkdown,
    ProjectSelector,
    ProfileSidebarProjectSummary,
    ProfileSidebarAvailability
  },
  props: {
    availability: {
      type: Object as PropType<AvailabilityInterface | null>,
      default: null
    },
    isInQuickInfo: {
      type: Boolean
    }
  },
  setup(props) {
    const { isInQuickInfo, availability } = toRefs(props);
    const projectName = ref('');
    const selectedProject = ref<ExternalProject | null>(null);
    const projectNameDebounced = useDebounce(projectName, 250);
    const { isOpened: isMessengerOpened } = useMessengerState();
    const { userId, user } = useProfileState(ProfileStoreKey.PROFILE);
    const { displayName: userName } = useUser(user);

    const { isAdmin: isCurrentUserAdmin } = useUser('self');
    const isAvailable = computed(() => availability.value?.available || isCurrentUserAdmin.value);

    const projectList = ref<ExternalProject[]>([]);
    const isProjectSelectionBeingSynced = ref(false);
    let projectSearchRequestId = 0;

    const projectId = computed(() => selectedProject.value?.id ?? '');

    const {
      projectVerification,
      recommendationKey, messageKey, notMatchingReasons,
      cantRecommend: disabled,
      recommend,
      message,
      hasBeenRecommended,
      verifiedProject
    } = useRecommendation(projectId, userId);

    const resultMessageKey = computed(() => (!recommendationKey.value ? null : messageKey.value));

    const resetRecommendationState = (clearSelectedProject = true) => {
      if (clearSelectedProject) {
        selectedProject.value = null;
      }
      hasBeenRecommended.value = false;
      message.value = '';
    };

    const select = (item: any) => {
      if (!item) {
        resetRecommendationState();
        return;
      }

      selectedProject.value = item;
      isProjectSelectionBeingSynced.value = true;
      projectName.value = item.title;
      resetRecommendationState(false);

      if (!isInQuickInfo.value) isMessengerOpened.value = false;

      nextTick(() => {
        isProjectSelectionBeingSynced.value = false;
      });
    };

    watch(projectNameDebounced, async (searchValue) => {
      const normalizedSearch = searchValue.trim().toLocaleLowerCase();
      const requestId = ++projectSearchRequestId;

      if (!normalizedSearch) {
        projectList.value = [];
        return;
      }

      projectList.value = [];

      try {
        const response = await recommendationService.searchProjects(normalizedSearch);
        if (requestId === projectSearchRequestId) {
          projectList.value = response.externalProjectList;
        }
      } catch (error) {
        if (requestId === projectSearchRequestId) {
          projectList.value = [];
        }
        console.error('Project recommendation search failed', error);
      }
    }, { immediate: true });

    watch(projectName, (value) => {
      if (isProjectSelectionBeingSynced.value) return;
      if (selectedProject.value && value !== selectedProject.value.title) {
        resetRecommendationState(false);
      }
    });

    return {
      resultMessageKey,
      projectName,
      projectList,
      projectVerification,
      verifiedProject,
      select,
      selectedProject,
      recommend,
      disabled,
      message,
      userId,
      userName,
      user,
      hasBeenRecommended,
      notMatchingReasons,
      isAvailable
    };
  }
});
